import type { ReactNode } from "react";
import type { LandmarkKey } from "./themes";

/**
 * Each NOC's landmark, drawn in the app as one silhouette on a 400 × 170
 * stage (ground at y 160), coloured by the banner's CSS variables:
 * `--wb-ink` the silhouette, `--wb-gold` its details, `--wb-sun` the sky.
 * Simplified on purpose — a recognisable outline at 6–30rem wide, not an
 * illustration. The moving parts carry the banner's animation classes
 * (`wb-spin`, `wb-puff`, `wb-bob`, `wb-sway`, `wb-shimmer`, `wb-twinkle`;
 * welcome.css), which all stop under `prefers-reduced-motion`.
 *
 *   MAS  Petronas Twin Towers, KL Tower        BRU  Omar Ali Saifuddien Mosque
 *   CAM  Angkor Wat, sugar palms               INA  Borobudur, a smoking Merapi
 *   LAO  Pha That Luang                        MYA  Shwedagon Pagoda
 *   PHI  Mayon, the flag's sun, a bahay kubo   SGP  Marina Bay Sands, Supertrees, the Flyer
 *   THA  Wat Arun's prangs, a temple roof      TLS  Cristo Rei of Dili, an uma lulik
 *   VIE  Ha Long Bay's karsts, a junk
 */
export function Landmark({ name }: { name: LandmarkKey }) {
	return (
		<svg className="wb__scene" viewBox="0 0 400 170" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false">
			{SCENES[name]()}
		</svg>
	);
}

/** A soft disc behind the landmark; pulses (`wb-sun`). */
const Sun = ({ cx = 292, cy = 58, r = 30 }: { cx?: number; cy?: number; r?: number }) => (
	<circle className="wb-sun" cx={cx} cy={cy} r={r} fill="var(--wb-sun)" />
);

/** A gently lapping strip of water along the ground. */
const Water = ({ x = 40, w = 330, y = 151 }: { x?: number; w?: number; y?: number }) => (
	<g className="wb-shimmer" fill="var(--wb-ink)">
		<rect x={x} y={y} width={w} height="9" opacity="0.1" />
		<rect x={x + 20} y={y + 3} width={w * 0.3} height="1.4" opacity="0.28" />
		<rect x={x + w * 0.55} y={y + 6} width={w * 0.25} height="1.4" opacity="0.22" />
	</g>
);

/** Three puffs that rise and fade from (x, y). */
const Smoke = ({ x, y }: { x: number; y: number }) => (
	<g fill="var(--wb-ink)">
		{[0, 1, 2].map((i) => (
			<circle key={i} className="wb-puff" style={{ animationDelay: `${i * -2}s` }} cx={x} cy={y} r={6 + i} opacity="0.2" />
		))}
	</g>
);

/** A lotus-bud tower (Angkor, That Luang): a pointed bulb from `base` up to `top`. */
const bud = (x: number, top: number, w: number, base: number) => {
	const h = base - top;
	return `M${x - w} ${base} C${x - w} ${base - h * 0.45} ${x - w * 0.55} ${top + h * 0.25} ${x} ${top} C${x + w * 0.55} ${top + h * 0.25} ${x + w} ${base - h * 0.45} ${x + w} ${base} Z`;
};

/** A small bell stupa (Borobudur's terraces). */
const bell = (x: number, base: number, w = 5) => `M${x - w} ${base} Q${x - w} ${base - 8} ${x} ${base - 9} Q${x + w} ${base - 8} ${x + w} ${base} Z M${x - 0.6} ${base - 9} h1.2 v-4 h-1.2 Z`;

/** A Petronas tower: stepped setbacks to a needle spire. */
const petronas = (c: number) =>
	`M${c - 13} 160 V72 L${c - 11} 66 V52 L${c - 8} 46 V36 L${c - 5} 32 V26 L${c - 2} 22 L${c} 2 L${c + 2} 22 L${c + 5} 26 V32 L${c + 8} 36 V46 L${c + 11} 52 V66 L${c + 13} 72 V160 Z`;

/** A Khmer-style prang (Wat Arun): tiered, narrowing, a spike on top. */
const prang = (x: number, h: number, w: number, base = 150) => {
	const steps: Array<[number, number]> = [
		[1, 0],
		[0.86, 0.2],
		[0.72, 0.2],
		[0.62, 0.42],
		[0.5, 0.42],
		[0.4, 0.62],
		[0.3, 0.62],
		[0.2, 0.8],
		[0.08, 0.8],
	];
	const left = steps.map(([fx, fy]) => `${(x - w * fx).toFixed(1)} ${(base - h * fy).toFixed(1)}`);
	const right = [...steps].reverse().map(([fx, fy]) => `${(x + w * fx).toFixed(1)} ${(base - h * fy).toFixed(1)}`);
	return `M${left.join(" L")} L${x} ${base - h * 1.1} L${right.join(" L")} Z`;
};

/** A sugar / coconut palm crown: fronds around (x, y). */
const Crown = ({ x, y, size = 11 }: { x: number; y: number; size?: number }) => (
	// The sway is on an inner group: a CSS transform would replace the outer one's `translate`.
	<g transform={`translate(${x} ${y})`}>
		<g className="wb-sway">
			{[-150, -110, -70, -30, 10, 50, 200, 240].map((a) => (
				<ellipse key={a} rx={size} ry={2.2} transform={`rotate(${a}) translate(${size - 1} 0)`} />
			))}
		</g>
	</g>
);

const SCENES: Record<LandmarkKey, () => ReactNode> = {
	MAS: () => (
		<>
			<Sun cx={300} cy={52} />
			<g fill="var(--wb-ink)">
				<g opacity="0.45">
					<rect x="40" y="120" width="22" height="40" />
					<rect x="66" y="102" width="16" height="58" />
					<rect x="86" y="128" width="24" height="32" />
					<rect x="118" y="112" width="18" height="48" />
					<rect x="262" y="116" width="20" height="44" />
					<rect x="346" y="122" width="26" height="38" />
				</g>
				<path d={petronas(172)} />
				<path d={petronas(222)} />
				<rect x="185" y="90" width="24" height="4" />
				<path d="M187 94 L197 110 L207 94" fill="none" stroke="var(--wb-ink)" strokeWidth="2" />
				<rect x="309" y="62" width="6" height="98" />
				<ellipse cx="312" cy="58" rx="13" ry="7" />
				<rect x="311" y="20" width="2" height="34" />
				<path d="M300 160 L306 146 H318 L324 160 Z" />
			</g>
			<g fill="var(--wb-gold)" className="wb-twinkle">
				<circle cx="172" cy="4" r="1.8" />
				<circle cx="222" cy="4" r="1.8" style={{ animationDelay: "-1.2s" }} />
				<circle cx="312" cy="21" r="1.8" style={{ animationDelay: "-0.6s" }} />
			</g>
		</>
	),
	BRU: () => (
		<>
			<Sun cx={128} cy={60} r={26} />
			<Water x={40} w={330} y={151} />
			<g fill="var(--wb-ink)">
				<path d="M98 151 V124 H302 V151 Z" />
				<rect x="176" y="100" width="48" height="24" />
				<rect x="112" y="112" width="40" height="12" />
				<rect x="248" y="112" width="40" height="12" />
				<path d="M120 112 Q132 94 144 112 Z" />
				<path d="M256 112 Q268 94 280 112 Z" />
				<rect x="318" y="56" width="12" height="95" />
				<rect x="314.5" y="56" width="19" height="5" />
				<path d="M318 56 Q324 42 330 56 Z" />
				<rect x="323.3" y="30" width="1.4" height="14" />
				<path d="M52 151 Q78 160 108 151 L102 146 H58 Z" className="wb-bob" />
			</g>
			<g fill="var(--wb-gold)">
				<path d="M174 100 C170 82 186 70 200 56 C214 70 230 82 226 100 Z" />
				<rect x="199" y="36" width="2" height="22" />
				<circle className="wb-twinkle" cx="200" cy="35" r="2.6" />
				<g opacity="0.85">
					{[110, 130, 150, 170, 190, 210, 230, 250, 270, 290].map((x) => (
						<path key={x} d={`M${x - 5} 151 V139 Q${x} 132 ${x + 5} 139 V151 Z`} />
					))}
				</g>
			</g>
		</>
	),
	CAM: () => (
		<>
			<Sun cx={200} cy={44} r={36} />
			<Water x={60} w={290} y={152} />
			<g fill="var(--wb-ink)">
				<path d="M70 152 V140 H330 V152 Z" />
				<path d="M95 140 V124 H305 V140 Z" />
				<path d="M125 124 V110 H275 V124 Z" />
				<path d={bud(200, 20, 17, 110)} />
				<path d={bud(160, 50, 13, 124)} />
				<path d={bud(240, 50, 13, 124)} />
				<path d={bud(115, 70, 11, 140)} />
				<path d={bud(285, 70, 11, 140)} />
				<rect x="38.8" y="96" width="2.6" height="56" />
				<rect x="360.8" y="104" width="2.6" height="48" />
				<Crown x={40} y={96} />
				<Crown x={362} y={104} size={10} />
			</g>
			<g fill="var(--wb-gold)" opacity="0.9">
				{[116, 128, 140].map((y) => (
					<rect key={y} x={y === 116 ? 186 : y === 128 ? 189 : 192} y={y - 64} width={y === 116 ? 28 : y === 128 ? 22 : 16} height="1.6" />
				))}
			</g>
		</>
	),
	INA: () => (
		<>
			<Sun cx={110} cy={52} r={26} />
			<path d="M226 160 L298 70 Q310 60 322 70 L394 160 Z" fill="var(--wb-ink)" opacity="0.28" />
			<Smoke x={310} y={58} />
			<g fill="var(--wb-ink)">
				<path d="M78 160 V146 H322 V160 Z" />
				<path d="M96 146 V134 H304 V146 Z" />
				<path d="M112 134 V123 H288 V134 Z" />
				<path d="M128 123 V113 H272 V123 Z" />
				<path d="M144 113 V104 H256 V113 Z" />
				<path d={[150, 162.5, 175, 187.5, 212.5, 225, 237.5, 250].map((x) => bell(x, 104)).join(" ")} />
				<path d="M158 104 V96 H242 V104 Z" />
				<path d={[166, 178, 222, 234].map((x) => bell(x, 96, 4.5)).join(" ")} />
				<path d="M170 96 V89 H230 V96 Z" />
				<path d="M178 89 Q178 64 200 62 Q222 64 222 89 Z" />
				<path d="M197 63 L200 38 L203 63 Z" />
			</g>
			<g fill="var(--wb-gold)">
				<rect x="78" y="146" width="244" height="1.6" />
				<rect x="112" y="123" width="176" height="1.6" />
			</g>
		</>
	),
	LAO: () => (
		<>
			<Sun cx={300} cy={56} r={28} />
			<g fill="var(--wb-ink)">
				<path d="M66 160 V140 H334 V160 Z" />
				<path d={Array.from({ length: 17 }, (_, i) => 78 + i * 15.25).map((x) => `M${x - 4} 140 L${x} 125 L${x + 4} 140 Z`).join(" ")} />
				<rect x="180" y="146" width="40" height="14" fill="var(--wb-gold)" />
			</g>
			<g fill="var(--wb-gold)">
				<path d="M120 140 V126 H280 V140 Z" />
				<path d="M140 126 V114 H260 V126 Z" />
				<path d={[128, 146, 164, 236, 254, 272].map((x) => bud(x, 94, 4.5, 126)).join(" ")} />
				<path d="M150 114 Q150 92 200 86 Q250 92 250 114 Z" />
				<path d="M184 88 C186 60 196 40 200 8 C204 40 214 60 216 88 Z" />
			</g>
			<g fill="var(--wb-ink)" opacity="0.35">
				<rect x="120" y="132" width="160" height="1.4" />
				<rect x="150" y="104" width="100" height="1.4" />
			</g>
			<circle className="wb-twinkle" cx="200" cy="9" r="2.4" fill="var(--wb-ink)" />
		</>
	),
	MYA: () => (
		<>
			<Sun cx={104} cy={54} r={28} />
			<g fill="var(--wb-ink)">
				<path d="M44 160 Q120 136 200 134 Q280 136 356 160 Z" />
				<path d="M90 138 C92 126 98 124 100 116 C102 124 108 126 110 138 Z M99.2 116 L100 100 L100.8 116 Z" />
				<path d="M290 138 C292 126 298 124 300 116 C302 124 308 126 310 138 Z M299.2 116 L300 100 L300.8 116 Z" />
			</g>
			<g fill="var(--wb-gold)">
				<path d="M128 136 V126 H272 V136 Z" />
				<path d="M140 126 V118 H260 V126 Z" />
				<path d="M146 118 C152 96 176 92 182 80 H218 C224 92 248 96 254 118 Z" />
				<rect x="182" y="72" width="36" height="8" />
				<rect x="185.5" y="66" width="29" height="6" />
				<path d="M190 66 C193 46 197 30 200 8 C203 30 207 46 210 66 Z" />
				<path d="M116 136 C118 124 124 122 126 114 C128 122 134 124 136 136 Z" />
				<path d="M264 136 C266 124 272 122 274 114 C276 122 282 124 284 136 Z" />
			</g>
			<g fill="var(--wb-ink)" opacity="0.3">
				<rect x="182" y="79" width="36" height="1.2" />
				<rect x="146" y="117" width="108" height="1.2" />
			</g>
			<circle className="wb-twinkle" cx="200" cy="7" r="2.6" fill="#fff" stroke="var(--wb-gold)" strokeWidth="1" />
		</>
	),
	PHI: () => (
		<>
			<g className="wb-spin" style={{ transformOrigin: "296px 50px" }} fill="var(--wb-sun)">
				<circle cx="296" cy="50" r="17" />
				{[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
					<path key={a} d="M296 22 L292 30 L296 28 L300 30 Z M296 22 L296 14" transform={`rotate(${a} 296 50)`} stroke="var(--wb-sun)" strokeWidth="1.6" />
				))}
			</g>
			<Smoke x={200} y={46} />
			<g fill="var(--wb-ink)">
				<path d="M66 160 Q158 134 188 54 Q200 44 212 54 Q242 134 334 160 Z" />
				<path d="M48 160 Q42 128 58 98" fill="none" stroke="var(--wb-ink)" strokeWidth="3.2" />
				<Crown x={58} y={98} size={13} />
				<rect x="322" y="140" width="2" height="20" />
				<rect x="348" y="140" width="2" height="20" />
				<rect x="318" y="124" width="36" height="17" />
				<path d="M310 127 L336 102 L362 127 Z" />
			</g>
			<g fill="var(--wb-gold)">
				<rect x="330" y="129" width="10" height="9" />
				<path d="M182 64 Q200 58 218 64 L214 72 Q200 68 186 72 Z" opacity="0.55" />
			</g>
		</>
	),
	SGP: () => (
		<>
			<Sun cx={96} cy={48} r={24} />
			<g fill="var(--wb-ink)">
				<g opacity="0.45">
					<rect x="118" y="108" width="16" height="52" />
					<rect x="272" y="120" width="14" height="40" />
				</g>
				<path d="M160 160 L158 56 H180 L178 160 Z" />
				<path d="M190 160 L188 56 H210 L208 160 Z" />
				<path d="M220 160 L218 56 H240 L238 160 Z" />
				<path d="M150 56 V49 H254 L266 44 L262 56 Z" />
				{[60, 92].map((x) => (
					<g key={x}>
						<path d={`M${x - 3} 160 L${x - 1.5} 112 H${x + 1.5} L${x + 3} 160 Z`} />
						<path d={`M${x - 16} 100 H${x + 16} L${x + 2} 113 H${x - 2} Z`} />
					</g>
				))}
				<path d="M306 160 L322 100 L338 160" fill="none" stroke="var(--wb-ink)" strokeWidth="3" />
			</g>
			<g className="wb-spin wb-spin--slow" style={{ transformOrigin: "322px 98px" }}>
				<circle cx="322" cy="98" r="44" fill="none" stroke="var(--wb-ink)" strokeWidth="3" />
				<g stroke="var(--wb-ink)" strokeWidth="0.9" opacity="0.6">
					{[0, 30, 60, 90, 120, 150].map((a) => (
						<line key={a} x1="278" y1="98" x2="366" y2="98" transform={`rotate(${a} 322 98)`} />
					))}
				</g>
				<g fill="var(--wb-gold)">
					{Array.from({ length: 12 }, (_, i) => (
						<circle key={i} cx="322" cy="54" r="2.8" transform={`rotate(${i * 30} 322 98)`} />
					))}
				</g>
			</g>
			<g fill="var(--wb-gold)" className="wb-twinkle">
				{[48, 56, 64, 72, 80, 88, 96, 104].map((x) => (
					<circle key={x} cx={x} cy={99} r="1.3" />
				))}
			</g>
		</>
	),
	THA: () => (
		<>
			<Sun cx={104} cy={50} r={26} />
			<Water x={40} w={330} y={152} />
			<g fill="var(--wb-ink)">
				<path d="M110 152 V146 H290 V152 Z" />
				<path d={prang(200, 118, 26, 146)} />
				<path d={prang(146, 70, 15, 146)} />
				<path d={prang(254, 70, 15, 146)} />
				<path d="M306 152 V132 H362 V152 Z" />
				<path d="M298 133 L334 104 L370 133 Z" />
				<path d="M308 116 L334 92 L360 116 Z" />
				<path d="M298 133 q-5 -5 -1 -11 M370 133 q5 -5 1 -11 M308 116 q-4 -4 0 -9 M360 116 q4 -4 0 -9" fill="none" stroke="var(--wb-ink)" strokeWidth="1.6" />
			</g>
			<g fill="var(--wb-gold)">
				{[0.2, 0.42, 0.62].map((f) => (
					<rect key={f} x={200 - 26 * (0.86 - f * 0.6)} y={146 - 118 * f - 1} width={52 * (0.86 - f * 0.6)} height="1.6" />
				))}
				<path d="M326 152 V138 H342 V152 Z" />
			</g>
		</>
	),
	TLS: () => (
		<>
			<Sun cx={300} cy={50} r={24} />
			<Water x={40} w={330} y={152} />
			<g fill="var(--wb-ink)">
				<path d="M120 152 Q190 92 250 108 Q300 120 352 152 Z" />
				<path d="M201 86 L203 60 H207 L209 86 Z" />
				<circle cx="205" cy="55.5" r="3.4" />
				<path d="M203.6 64 L193 51 L195 49 L205 60 Z M206.4 64 L217 51 L215 49 L205 60 Z" />
				<rect x="350" y="140" width="2" height="12" />
				<rect x="370" y="140" width="2" height="12" />
				<rect x="346" y="128" width="30" height="13" />
				<path d="M342 131 Q360 74 361 62 Q362 74 380 131 Z" />
				<g className="wb-bob">
					<path d="M56 152 Q78 160 102 152 Z" />
					<path d="M78 151 V124 L94 148 Z" />
				</g>
			</g>
			<circle cx="205" cy="92" r="8" fill="var(--wb-sun)" stroke="var(--wb-ink)" strokeWidth="1.2" />
			<g fill="var(--wb-gold)">
				<path d="M350 128 L361 98 L372 128 Z" opacity="0.5" />
			</g>
		</>
	),
	VIE: () => (
		<>
			<Sun cx={208} cy={46} r={26} />
			<Water x={40} w={340} y={148} />
			<g fill="var(--wb-ink)">
				<g opacity="0.4">
					<path d="M150 150 C150 120 166 100 182 102 C198 104 200 128 198 150 Z" />
					<path d="M330 150 C328 132 342 118 356 120 C368 122 370 138 368 150 Z" />
				</g>
				<path d="M58 150 C56 120 68 96 80 92 C94 90 102 112 100 150 Z" />
				<path d="M104 150 C100 100 116 62 134 60 C154 60 160 104 156 150 Z" />
				<path d="M248 150 C244 112 258 86 274 86 C290 88 294 118 290 150 Z" />
				<path d="M298 150 C296 126 308 108 322 108 C336 110 340 130 338 150 Z" />
			</g>
			<g className="wb-bob">
				<path d="M176 148 Q200 158 228 148 L224 142 H180 Z" fill="var(--wb-ink)" />
				<g fill="var(--wb-gold)">
					<path d="M188 141 V104 Q176 114 174 128 Q176 136 188 141 Z" />
					<path d="M203 141 V94 Q189 106 187 124 Q189 134 203 141 Z" />
					<path d="M218 141 V108 Q206 116 204 130 Q206 137 218 141 Z" />
				</g>
				<g stroke="var(--wb-ink)" strokeWidth="0.8" opacity="0.5">
					<line x1="178" y1="118" x2="188" y2="118" />
					<line x1="191" y1="112" x2="203" y2="112" />
					<line x1="207" y1="122" x2="218" y2="122" />
				</g>
			</g>
		</>
	),
};
